import { OperatorFunction } from 'rxjs/interfaces';
import { Action } from '@ngrx/store';
import { filter } from 'rxjs/operators';
import * as routerActions from '../actions/router.actions';

/**
 * Initially there was ROUTER_NAVIGATION used to detect route changes navigation and then matching store slice
 * fetched from server. ROUTER_NAVIGATION is fired before any guards or resolvers run but we need to fetch config and
 * authentication (access token) first - so each particular @Effect() had to wait until application has been
 * initialized:
 * @Effect() routeChange$ = this.actions$
 *     .ofType(ROUTER_NAVIGATION)
 *     .combineLatest(this.configLoadedGuard.waitForAppToBeReady(), a => a)
 *     combineLatest(this.appReadyGuard.waitForAppToBeReady(), a => a)
 *     ...
 *
 * An inspiration has been this article from Victor Savkin: https://github.com/vsavkin/state_management_ngrx4/blob/master/clientapp/src/app/model.ts#L101
 *
 * But this implementation suffers from guards not be guarding ROUTER_NAVIGATION.
 * In other words @Effect() had fired an event to load data before config and authentication (access token) was
 * available.
 *
 * Then we used an idea described here: https://medium.com/@amcdnl/angular-routing-data-with-ngrx-effects-1cda1bd5e579
 * @param {string | string[]} route
 * @return {OperatorFunction<Action, Action>}
 */
export function ofRoute(route: string | string[]): OperatorFunction<Action, Action> {
    return filter((action: Action) => {
        const isRouteAction = action.type === routerActions.ROUTE_CHANGE;
        if (isRouteAction) {
            const routeAction = action as routerActions.RouteChange;
            const routePath = routeAction.payload.path;
            if (Array.isArray(route)) {
                return route.indexOf(routePath) > -1;
            } else {
                return routePath === route;
            }
        }
        return isRouteAction;
    });
}

/**
 * Operator used to detect inter-modules routing - it can be used to clear application state slices. For example when
 * there is a navigation from applications module to card module we need to clear state (due to double logo fetching
 * error SSF-233).
 * @param {string} route
 * @return {OperatorFunction<Action, Action>}
 */
export function ofRouteExit(route: string): OperatorFunction<Action, Action> {
    return filter((action: Action) => {
        const isRouteAction = action.type === routerActions.ROUTE_CHANGE;
        if (isRouteAction) {
            const routeAction = action as routerActions.RouteChange;
            return routeAction.payload.previousPath === route  &&  routeAction.payload.path !== route;
        }
        return isRouteAction;
    });
}



// WEBPACK FOOTER //
// ./~/angular2-template-loader!./~/@angularclass/hmr-loader!./~/angular-router-loader/src!./~/tslint-loader!./src/app/core/router/route-operator.ts